import { StoredItem } from "./Metadata";
import "core-js/proposals/array-buffer-base64";

// Unfortunately this doesn't work with unit tests running in nodejs. I wish we
// had rust-like conditional compilation.
const crypto = window.crypto;

// For AES-GCM 256.
const IV_SIZE = 12;

export type DataContainer = { kdf: number; data: string };
type UrlContainer = {
  kdf: number;
  url: string;
};
type Container = DataContainer | UrlContainer;

// For now nested confidential entries are not allowed/used.
export async function decrypt(
  container: Container,
  rootKey: CryptoKey,
): Promise<StoredItem> {
  if ("data" in container) {
    const data = Uint8Array.fromBase64(container.data);
    const json_bytes = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: data.subarray(0, IV_SIZE) },
      await derive(rootKey, container.kdf),
      data.subarray(IV_SIZE),
    );
    // TODO: Shouldn't we have to handle non-compliance with out types?
    return JSON.parse(new TextDecoder().decode(json_bytes));
  } else {
    throw "decrypting with 'url' not yet supported"; // TODO
  }
}

export async function encrypt(
  item: StoredItem,
  rootKey: CryptoKey,
  kdf: number,
): Promise<DataContainer> {
  console.log("encrypting:", item);
  const json_bytes = new TextEncoder().encode(JSON.stringify(item));
  const iv = crypto.getRandomValues(new Uint8Array(IV_SIZE));
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    await derive(rootKey, kdf),
    json_bytes,
  );
  // There are probably better ways to do this.
  const data = new Uint8Array(IV_SIZE + encrypted.byteLength);
  data.set(iv, 0);
  data.set(new Uint8Array(encrypted), IV_SIZE);
  return {
    data: data.toBase64(),
    kdf,
  };
}

async function derive(rootKey: CryptoKey, kdf: number): Promise<CryptoKey> {
  // See https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/deriveKey
  return await crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new Int32Array(),
      info: new Int32Array([kdf]),
    },
    rootKey,
    { name: "AES-GCM", length: 256 },
    false, // Not exportable
    ["encrypt", "decrypt"],
  );
}

export async function generateRootKey(): Promise<[CryptoKey, Uint8Array]> {
  // This is ridiculous. For some reason I still don't understand this API
  // doesn't let us export keys intended for key derivation. The best argument
  // I've read so far is that it isn't needed because we have to import the key.
  // Unfortunately this means we have to return them separately
  //
  // https://github.com/w3c/webcrypto/issues/76
  //
  // Apparently Firefox doesn't bother about the value of extractable, but
  // chrome returns an error if set to true.
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const key = await crypto.subtle.importKey("raw", bytes, "HKDF", false, [
    "deriveKey",
  ]);
  return [key, bytes];
}

export async function importRootKey(bytes: Uint8Array): Promise<CryptoKey> {
  // See comment in generateRootKey.
  return crypto.subtle.importKey("raw", bytes, "HKDF", false, ["deriveKey"]);
}
